interface SeoInput {
  title: string
  description: string
  image?: string
  jsonLd?: Record<string, unknown>
  /**
   * Ścieżka kanoniczna podana wprost, BEZ query — karta produktu: `?rozmiar=`
   * to wariant tej samej strony, nie osobny adres. Brak = bieżący adres bez
   * parametrów śledzących (patrz canonicalUrl).
   */
  canonicalPath?: string
}

/**
 * Parametry kampanii (plus `utm_*` prefiksem) — nigdy nie należą do adresu
 * kanonicznego ani og:url: z nimi każde kliknięcie reklamy dawało osobny
 * „kanoniczny" adres w HTML z serwera.
 */
const TRACKING_PARAMS = new Set(['gclid', 'fbclid', 'gbraid', 'wbraid', 'msclkid', '_gl'])

function isTrackingParam(key: string): boolean {
  const normalized = key.toLowerCase()
  return normalized.startsWith('utm_') || TRACKING_PARAMS.has(normalized)
}

/**
 * Adres kanoniczny z bieżącego adresu: wycina WYŁĄCZNIE parametry śledzące.
 * Inne zostają — na listingach `?rozmiar=` czy `?kategorie=` to filtry
 * (AmazzingFilter), czyli inna treść strony.
 *
 * Filtrujemy SUROWE pary query zamiast `url.searchParams.delete()`: to drugie
 * przepisuje całe query („,” → %2C, %20 → +), więc ten sam listing miałby inny
 * napis canonicala z reklamy niż z ruchu organicznego.
 */
export function canonicalUrl(fullPath: string, siteUrl: string): string {
  const url = new URL(fullPath || '/', siteUrl)
  const kept = url.search.slice(1).split('&').filter((pair) => {
    if (!pair) return false
    const rawKey = pair.split('=', 1)[0] ?? ''
    let key = rawKey
    try {
      key = decodeURIComponent(rawKey.replace(/\+/g, ' '))
    }
    catch {
      // Niepoprawne kodowanie klucza — porównujemy surowy napis.
    }
    return !isTrackingParam(key)
  })
  url.search = kept.length ? `?${kept.join('&')}` : ''
  return url.toString()
}

export interface ProductJsonLdInput {
  name: string
  description: string
  image: string
  sku: string
  brand: string
  price: number
  currency?: string
  availability?: 'InStock' | 'OutOfStock' | 'PreOrder'
  url: string
}

export function buildProductJsonLd(product: ProductJsonLdInput): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    'name': product.name,
    'description': product.description,
    'image': product.image,
    'sku': product.sku,
    'brand': {
      '@type': 'Brand',
      'name': product.brand,
    },
    'offers': {
      '@type': 'Offer',
      'price': product.price,
      'priceCurrency': product.currency ?? 'PLN',
      'availability': `https://schema.org/${product.availability ?? 'InStock'}`,
      'url': product.url,
    },
  }
}

/**
 * SEO strony. Strona, której dane dochodzą z zapytania (produkt, CMS, marka),
 * podaje GETTER i woła composable wprost w setupie — nie z `watchEffect`.
 *
 * Dlaczego: na serwerze `watchEffect` wykonuje się raz, w setupie, zanim
 * zapytanie się rozstrzygnie (onServerPrefetch), i już nie wraca. Rejestracja
 * z jego wnętrza dawała w HTML z serwera sam titleTemplate z pustym `%s`
 * („ · LAVMAG"), a tytuł „naprawiał się" dopiero po hydracji. Getter jest
 * czytany leniwie — w chwili renderu head, gdy dane już są.
 *
 * Statyczny obiekt działa jak dotąd (adres kanoniczny z chwili wywołania).
 */
export function useStorefrontSeo(source: SeoInput | (() => SeoInput | null | undefined)) {
  const config = useRuntimeConfig()
  const route = useRoute()
  const isGetter = typeof source === 'function'
  const input = computed(() => (isGetter ? source() : source) ?? null)
  const toCanonical = () => {
    const path = input.value?.canonicalPath
    return path
      ? new URL(path, config.public.siteUrl).toString()
      : canonicalUrl(route.fullPath, config.public.siteUrl)
  }
  // Getter = strona żyje dłużej niż jeden adres (PDP przechodzi produkt → produkt
  // bez remountu), więc adres kanoniczny musi iść za trasą.
  const canonical = isGetter ? computed(toCanonical) : toCanonical()
  const image = computed(() =>
    input.value?.image ? new URL(input.value.image, config.public.siteUrl).toString() : undefined,
  )

  useSeoMeta({
    title: () => input.value?.title,
    description: () => input.value?.description,
    ogTitle: () => input.value?.title,
    ogDescription: () => input.value?.description,
    ogType: 'website',
    ogUrl: canonical,
    ogImage: image,
    twitterCard: () => (image.value ? 'summary_large_image' : 'summary'),
  })

  useHead({
    link: () => [{ rel: 'canonical', href: toValue(canonical) }],
    script: () => input.value?.jsonLd
      ? [{ type: 'application/ld+json', innerHTML: JSON.stringify(input.value.jsonLd) }]
      : [],
  })
}
